import { API_BASE_URL } from './api';

class ApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface BookingServiceItem {
  name: string;
  price: number;
}

export interface BookingService {
  booking_service_id: number;
  booking_id: number;
  service_id: number;
  quantity: number;
  price: string;
  created_at: string;
  service_name?: string;
  service_description?: string;
}

/**
 * NOTE:
 * booking_number is a string in the API (e.g. "CW251119000112"),
 * so using string here to avoid inconsistencies.
 */
export interface Booking {
  booking_id: number;
  booking_number: string;
  user_id: number;
  vehicle_id: number;
  address_id?: number;
  booking_date: string;
  start_time: string;
  end_time: string;
  status:
    | 'pending'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'rejected'
    | 'assign_washer'
    | 'pickup'
    | 'work_in_progress'
    | 'drop'
    | 'arrived_at_the_location'
    | 'at_the_service_station'
    | 'start_service'
    | 'end_service';
  total_amount: number;
  admin_commission?: number;
  discount_amount: number;
  tax_amount: number;
  tip_amount:number;
  final_amount: number;
  payment_status: string;
  payment_method?: string | null;
  notes?: string | null;
  customer_name: string;
  customer_email: string;
  customer_mobile: string;
  registration_number: string;
  car_brand: string;
  car_model: string;
  model_name?: string;
  brand_name?: string;
  number_plate?: string;
  services?: BookingServiceItem[];
  address_line1?: string;
  address_line2?: string | null;
  city?: string;
  state?: string;
  postal_code?: string;
  created_at?: string;
  updated_at?: string;
  assigned_staff_id?: number | null;
  staff_name?: string | null;
  staff_mobile?: string | null;
  contact_person_name?: string | null;
  contact_person_number?: string | null;
  cancellation_reason?: string;
  refund_status?: string;
  refund_amount?: number;
  assigned_staff?: AssignedStaff | null;
  beforeImages?: Array<{ booking_id: number; image_id: number; image_name_url: string }>;
  afterImages?: Array<{ booking_id: number; image_id: number; image_name_url: string }>;
}

export interface AssignedStaff {
  sp_id?: number;
  sp_name?: string;
  sp_email?: string | null;
  sp_mobile?: string | null;
  sp_profile_photo?: string | null;
}


interface InterestedSP {
  interest_id: number;
  sp_id: number;
  sp_name: string;
  sp_mobile: string;
}

/**
 * Generic fetch helper with unified error handling
 */
async function apiFetch(url: string, options?: RequestInit) {
  const fullUrl = `${API_BASE_URL}${url}`;
  // console.log('📡 Fetching:', fullUrl, options);

  const response = await fetch(fullUrl, {
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    ...options,
  });

  const contentType = response.headers.get('content-type');
  const data =
    contentType && contentType.includes('application/json')
      ? await response.json()
      : await response.text();

  if (!response.ok) {
    console.error('❌ API error', response.status, data);
    // data may be string or object
    const message = typeof data === 'string' ? data : data?.message || 'API Error';
    throw new ApiError(message, response.status);
  }

  return data;
}

/* -------------------------
   📘 GET: All Bookings (with pagination)
------------------------- */
export const getBookings = async (
  page: number = 1,
  limit: number = 10,
  status?: string,
  date?: string
): Promise<{ bookings: Booking[]; pagination?: any }> => {
  try {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (status) params.append('status', status);
    if (date) params.append('booking_date', date);

    const data = await apiFetch(`/bookings?${params.toString()}`, {
      method: 'GET',
      cache: 'no-store',
    });

    // Handle both array and object responses
    let bookingsData: any[] = [];
    let pagination: any = {};

    if (Array.isArray(data)) {
      bookingsData = data;
    } else if (data?.data?.bookings) {
      bookingsData = data.data.bookings;
      pagination = data.data.pagination || {};
    } else if (Array.isArray(data?.data)) {
      bookingsData = data.data;
      pagination = data.pagination || {};
    } else if (Array.isArray(data?.bookings)) {
      bookingsData = data.bookings;
      pagination = data.pagination || {};
    } else {
      console.warn('Unexpected response format from getBookings:', data);
      return { bookings: [], pagination: {} };
    }

    // Transform bookings
    const transformed: Booking[] = bookingsData.map((b: any) => ({
      booking_id: b.booking_id,
      booking_number: String(b.booking_number ?? ''),
      user_id: b.user_id,
      vehicle_id: b.vehicle_id,
      address_id: b.address_id,
      booking_date: b.booking_date,
      start_time: b.start_time,
      end_time: b.end_time,
      status: b.status,
      total_amount: parseFloat(b.total_amount) || 0,
      discount_amount: parseFloat(b.discount_amount) || 0,
      tax_amount: parseFloat(b.tax_amount) || 0,
      tip_amount: parseFloat(b.tip_amount) || 0,
      final_amount: parseFloat(b.final_amount) || 0,
      payment_status: b.payment_status,
      payment_method: b.payment_method,
      notes: b.notes,
      customer_name: `Customer ${b.user_id}`,
      customer_email: '',
      customer_mobile: '',
      registration_number: '',
      car_brand: '',
      car_model: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      created_at: b.created_at,
      updated_at: b.updated_at,
      assigned_staff_id: b.assigned_staff_id ?? null,
      staff_name: '',
      staff_mobile: '',
      contact_person_name: b.contact_person_name,
      contact_person_number: b.contact_person_number || '',
      cancellation_reason: b.cancellation_reason,
      refund_status: b.refund_status,
      refund_amount: b.refund_amount,
      services: Array.isArray(b.services)
        ? b.services.map((s: any) => ({
            name: s.service_name || 'Unknown Service',
            price: parseFloat(s.price) || 0,
          }))
        : [],

      // map assigned_staff if present in list item
      assigned_staff: b.assigned_staff
        ? {
            sp_id: b.assigned_staff.sp_id,
            sp_name: b.assigned_staff.sp_name,
            sp_email: b.assigned_staff.sp_email,
            sp_mobile: b.assigned_staff.sp_mobile,
            sp_profile_photo: b.assigned_staff.sp_profile_photo ?? null,
          }
        : null,
    }));

    return { bookings: transformed, pagination };
  } catch (err: any) {
    console.error('Error fetching bookings:', err);
    throw new ApiError(err.message || 'Failed to fetch bookings');
  }
};

/* -------------------------
   📗 GET: Booking by ID
------------------------- */
export const getBookingById = async (id: number): Promise<Booking> => {
  try {
    // use apiFetch for consistent error handling/parsing
    const data = await apiFetch(`/bookings/${id}`, { method: 'GET' });
    // Properly unwrap nested structure from API response
    const booking = data?.data?.booking || data?.booking || data;
    if (!booking) throw new Error('Booking not found in response');

    // Transform response safely and include contact person fields
    const transformed: Booking = {
      booking_id: booking.booking_id,
      booking_number: String(booking.booking_number ?? ''),
      user_id: booking.user_id,
      vehicle_id: booking.vehicle_id,
      address_id: booking.address_id,
      booking_date: booking.booking_date,
      start_time: booking.start_time,
      end_time: booking.end_time,
      status: booking.status,
      total_amount: parseFloat(booking.total_amount) || 0,
      discount_amount: parseFloat(booking.discount_amount) || 0,
      tax_amount: parseFloat(booking.tax_amount) || 0,
      tip_amount: parseFloat(booking.tip_amount) || 0,
      final_amount: parseFloat(booking.final_amount) || 0,
      payment_status: booking.payment_status,
      payment_method: booking.payment_method,
      notes: booking.notes || '',

      // Contact person info
      contact_person_name: booking.contact_person_name || '',
      contact_person_number: booking.contact_person_number || '',

      // Customer & vehicle info (fallbacks)
      customer_name: booking.customer_name || `Customer ${booking.user_id}`,
      customer_email: booking.customer_email || '',
      customer_mobile: booking.customer_mobile || '',
      registration_number: booking.registration_number || booking.number_plate || '',
      car_brand: booking.car_brand || booking.brand_name || '',
      car_model: booking.car_model || booking.model_name || '',
      model_name: booking.model_name || booking.car_model || '',
      brand_name: booking.brand_name || booking.car_brand || '',
      number_plate: booking.number_plate || booking.registration_number || '',

      // Address info
      address_line1: booking.address_line1 || '',
      address_line2: booking.address_line2 || '',
      city: booking.city || '',
      state: booking.state || '',
      postal_code: booking.postal_code || '',

      // Audit & staff info
      created_at: booking.created_at,
      updated_at: booking.updated_at,
      assigned_staff_id: booking.assigned_staff_id ?? null,
      staff_name: booking.staff_name || '',
      staff_mobile: booking.staff_mobile || '',

      cancellation_reason: booking.cancellation_reason || '',
      refund_status: booking.refund_status || '',
      refund_amount: booking.refund_amount || 0,

      // Map services if they exist in the response
      services: Array.isArray(booking.services)
        ? booking.services.map((s: any) => ({
            name: s.service_name || 'Unknown Service',
            price: parseFloat(s.price) || 0,
          }))
        : [],

      // Map assigned_staff (important)
      assigned_staff: booking.assigned_staff
        ? {
            sp_id: booking.assigned_staff.sp_id,
            sp_name: booking.assigned_staff.sp_name,
            sp_email: booking.assigned_staff.sp_email,
            sp_mobile: booking.assigned_staff.sp_mobile,
            sp_profile_photo: booking.assigned_staff.sp_profile_photo ?? null,
          }
        : null,

      // Map before and after images if present
      beforeImages: Array.isArray(booking.beforeImages)
        ? booking.beforeImages.map((img: any) => ({
            booking_id: img.booking_id,
            image_id: img.image_id,
            image_name_url: img.image_name_url,
          }))
        : [],
      afterImages: Array.isArray(booking.afterImages)
        ? booking.afterImages.map((img: any) => ({
            booking_id: img.booking_id,
            image_id: img.image_id,
            image_name_url: img.image_name_url,
          }))
        : [],
    };

    return transformed;
  } catch (err: any) {
    console.error('Error fetching booking by ID:', err);
    throw new ApiError(err.message || 'Failed to fetch booking details');
  }
};

/* -------------------------
   📒 GET: Booking Services
------------------------- */
export const getBookingServices = async (
  bookingId: number
): Promise<BookingService[]> => {
  try {
    const data = await apiFetch(`/bookings/${bookingId}/services`, {
      method: 'GET',
    });

    let services: any[] = [];
    if (Array.isArray(data)) services = data;
    else if (Array.isArray(data?.data?.services)) services = data.data.services;
    else if (Array.isArray(data?.services)) services = data.services;
    else if (Array.isArray(data?.data)) services = data.data;

    return services;
  } catch (err: any) {
    console.error('Error fetching booking services:', err);
    if (err.status === 404) return [];
    return [];
  }
};

/* -------------------------
   🛠 PATCH: Update Status
------------------------- */
export const updateBookingStatus = async (
  id: number,
  status: string
): Promise<Booking> => {
  const data = await apiFetch(`/bookings/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

  const booking = data?.data?.booking || data?.booking || data || { booking_id: id, status };

  // return mapping includes assigned_staff if present
  return {
    booking_id: booking.booking_id || id,
    booking_number: String(booking.booking_number || ''),
    user_id: booking.user_id || 0,
    vehicle_id: booking.vehicle_id || 0,
    address_id: booking.address_id || 0,
    booking_date: booking.booking_date || '',
    start_time: booking.start_time || '',
    end_time: booking.end_time || '',
    status: booking.status || status,
    total_amount: parseFloat(booking.total_amount) || 0,
    discount_amount: parseFloat(booking.discount_amount) || 0,
    tax_amount: parseFloat(booking.tax_amount) || 0,
    tip_amount: parseFloat(booking.tip_amount) || 0,
    final_amount: parseFloat(booking.final_amount) || 0,
    payment_status: booking.payment_status || '',
    payment_method: booking.payment_method || '',
    notes: booking.notes || '',
    customer_name: booking.customer_name || `Customer ${booking.user_id || 'Unknown'}`,
    customer_email: booking.customer_email || '',
    customer_mobile: booking.customer_mobile || '',
    registration_number: booking.registration_number || '',
    car_brand: booking.car_brand || '',
    car_model: booking.car_model || '',
    address_line1: booking.address_line1 || '',
    address_line2: booking.address_line2 || '',
    city: booking.city || '',
    state: booking.state || '',
    postal_code: booking.postal_code || '',
    created_at: booking.created_at || '',
    updated_at: booking.updated_at || '',
    assigned_staff_id: booking.assigned_staff_id ?? null,
    staff_name: booking.staff_name || '',
    staff_mobile: booking.staff_mobile || '',
    services: Array.isArray(booking.services)
      ? booking.services.map((s: any) => ({
          name: s.service_name || 'Unknown Service',
          price: parseFloat(s.price) || 0,
        }))
      : [],
    assigned_staff: booking.assigned_staff
      ? {
          sp_id: booking.assigned_staff.sp_id,
          sp_name: booking.assigned_staff.sp_name,
          sp_email: booking.assigned_staff.sp_email,
          sp_mobile: booking.assigned_staff.sp_mobile,
          sp_profile_photo: booking.assigned_staff.sp_profile_photo ?? null,
        }
      : null,
  };
};

/* -------------------------
   👷 POST: Assign Staff
------------------------- */
export const assignStaffToBooking = async (
  bookingId: number,
  staffId: number
): Promise<{
  success: boolean;
  message: string;
  booking_id: number;
  staff_id: number;
  staff_name: string;
  staff_mobile: string;
  assigned_staff?: AssignedStaff | null;
}> => {
  const data = await apiFetch(`/bookings/${bookingId}/assign-staff`, {
    method: 'POST',
    body: JSON.stringify({ staff_id: staffId }),
  });

  const booking = data?.data?.booking || data?.booking || {};

  // Some APIs may also return staff details at top-level in data; try those fallbacks
  const staffFromResponse =
    data?.data?.assigned_staff || data?.assigned_staff || booking?.assigned_staff || null;

  const assigned_staff: AssignedStaff | null = staffFromResponse
    ? {
        sp_id: staffFromResponse.sp_id,
        sp_name: staffFromResponse.sp_name,
        sp_email: staffFromResponse.sp_email || '',
        sp_mobile: staffFromResponse.sp_mobile || '',
        sp_profile_photo: staffFromResponse.sp_profile_photo ?? null,
      }
    : null;

  return {
    success: true,
    message: data?.message || 'Staff assigned successfully',
    booking_id: booking.booking_id || bookingId,
    staff_id: staffId,
    staff_name: data?.data?.staff_name || data?.staff_name || assigned_staff?.sp_name || '',
    staff_mobile: data?.data?.staff_mobile || data?.staff_mobile || assigned_staff?.sp_mobile || '',
    assigned_staff,
  };
};

export const getInterestedSP = async (bookingId: number) => {
  const response = await fetch(
    `${API_BASE_URL}/interested-service-providers?booking_id=${bookingId}`
  );
  const data = await response.json();
  return data.data || [];
};

/* -------------------------
   🔄 POST: Update Booking Interest Status
------------------------- */
export const updateBookingInterestStatus = async (payload: {
  interest_id: number;
  interest_status: "Pending" | "Accepted" | "Rejected";
}) => {
  try {
    const data = await apiFetch(`/booking/interest/status`, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    return {
      success: true,
      message: data?.message || "Interest status updated",
      data: data?.data || null,
    };
  } catch (err: any) {
    console.error("Error updating interest status:", err);
    throw new ApiError(err.message || "Failed to update interest status");
  }
};
